import React, { useState, useEffect, useCallback } from 'react';
import { attendanceAPI } from '../../services/api';
import { theme } from '../../theme';

const INPUT = {
  border: `1px solid ${theme.border}`, borderRadius: '0.5rem',
  padding: '0.5rem 0.75rem', fontSize: '0.875rem',
  color: theme.text, background: '#fff', outline: 'none', boxSizing: 'border-box',
};

const MODE_LABEL  = { 0: 'Fingerprint', 1: 'Card', 2: 'Password', 3: 'Card+FP', 8: 'Face' };
const TABS = ['Today', 'History', 'Devices', 'Diagnostics'];

export default function Attendance() {
  const [tab, setTab] = useState('Today');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Biometric Attendance</h1>
        <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Live feed from AMS biometric terminals</p>
      </div>

      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: `1px solid ${theme.border}` }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            padding: '0.5rem 1.25rem', fontSize: '0.875rem', fontWeight: 500,
            border: 'none', background: 'none', cursor: 'pointer',
            borderBottom: tab === t ? `2px solid ${theme.primary}` : '2px solid transparent',
            color: tab === t ? theme.primary : theme.textMuted, marginBottom: '-1px',
          }}>{t}</button>
        ))}
      </div>

      {tab === 'Today'       && <TodayTab />}
      {tab === 'History'     && <HistoryTab />}
      {tab === 'Devices'     && <DevicesTab />}
      {tab === 'Diagnostics' && <DiagnosticsTab />}
    </div>
  );
}

// ── Today Tab ─────────────────────────────────────────────────────────────────
function TodayTab() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    attendanceAPI.getToday()
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      await attendanceAPI.sync({ start_date: new Date().toISOString().slice(0, 10) });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const relink = async () => {
    setSyncing(true);
    try {
      const r = await attendanceAPI.relink();
      alert(`Re-link done: ${r.data.relinked} records linked, ${r.data.skipped_no_match} still unmatched.`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Re-link failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* KPI row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem' }}>
        <KpiCard label="Total Punches" value={data?.total_punches ?? 0} color={theme.primary} icon="👆" />
        <KpiCard label="Currently IN"  value={data?.present_count ?? 0} color="#10B981" icon="✅" />
        <KpiCard label="Last OUT"      value={data?.absent_count  ?? 0} color="#F59E0B" icon="🚪" />
        <KpiCard label="Date" value={data?.date ?? '—'} color={theme.textMuted} icon="📅" />
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
        <button onClick={relink} disabled={syncing}
          style={{ background: '#6366F1', color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', fontSize: '0.875rem', cursor: 'pointer', opacity: syncing ? 0.6 : 1 }}>
          {syncing ? '…' : '🔗 Fix Unlinked Records'}
        </button>
        <button onClick={sync} disabled={syncing}
          style={{ background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', fontSize: '0.875rem', cursor: 'pointer', opacity: syncing ? 0.6 : 1 }}>
          {syncing ? 'Syncing…' : '🔄 Sync from AMS'}
        </button>
      </div>

      {/* Live feed table */}
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
        <div style={{ padding: '1rem', borderBottom: `1px solid ${theme.border}` }}>
          <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: theme.text }}>Today's Punch Log</h3>
        </div>
        {(!data?.all_today?.length) ? (
          <p style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted, fontSize: '0.875rem' }}>No punches recorded today</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: theme.pageBg, borderBottom: `1px solid ${theme.border}` }}>
                  {['Employee', 'Emp Code', 'Department', 'Time', 'Direction', 'Mode', 'Temp'].map(h => (
                    <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.all_today.map((r, i) => (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${theme.border}`, background: i % 2 === 0 ? '#fff' : theme.pageBg }}>
                    <td style={{ padding: '0.65rem 1rem', fontWeight: 500, color: theme.text }}>{r.employee_name || '—'}</td>
                    <td style={{ padding: '0.65rem 1rem', color: theme.textMuted, fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.emp_code || '—'}</td>
                    <td style={{ padding: '0.65rem 1rem', color: theme.textMuted }}>{r.department || '—'}</td>
                    <td style={{ padding: '0.65rem 1rem', color: theme.text, whiteSpace: 'nowrap' }}>
                      {r.record_time ? new Date(r.record_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '—'}
                    </td>
                    <td style={{ padding: '0.65rem 1rem' }}>
                      <span style={{
                        padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600,
                        background: r.in_out === 0 ? '#D1FAE5' : '#FEE2E2',
                        color: r.in_out === 0 ? '#065F46' : '#991B1B',
                      }}>{r.in_out_label}</span>
                    </td>
                    <td style={{ padding: '0.65rem 1rem', color: theme.textMuted, fontSize: '0.8rem' }}>{r.mode_label}</td>
                    <td style={{ padding: '0.65rem 1rem', color: theme.textMuted, fontSize: '0.8rem' }}>
                      {r.temperature ? `${r.temperature}°C` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── History Tab ───────────────────────────────────────────────────────────────
function HistoryTab() {
  const today = new Date().toISOString().slice(0, 10);
  const [filters, setFilters] = useState({ start_date: today, end_date: today, emp_code: '' });
  const [records, setRecords] = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage]       = useState(1);

  const load = useCallback((p = 1) => {
    setLoading(true);
    const params = { page: p, limit: 100 };
    if (filters.start_date) params.start_date = filters.start_date;
    if (filters.end_date)   params.end_date   = filters.end_date;
    if (filters.emp_code)   params.emp_code   = filters.emp_code;
    attendanceAPI.getRecords(params)
      .then(r => { setRecords(r.data.records || []); setTotal(r.data.total || 0); setPage(p); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [filters]);

  useEffect(() => { load(1); }, [load]);

  const syncRange = async () => {
    try {
      await attendanceAPI.sync({ start_date: filters.start_date, end_date: filters.end_date });
      load(1);
    } catch (e) {
      alert(e.response?.data?.detail || 'Sync failed');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: theme.textMuted, marginBottom: '0.25rem' }}>From</label>
          <input type="date" value={filters.start_date}
            onChange={e => setFilters(f => ({ ...f, start_date: e.target.value }))} style={INPUT} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: theme.textMuted, marginBottom: '0.25rem' }}>To</label>
          <input type="date" value={filters.end_date}
            onChange={e => setFilters(f => ({ ...f, end_date: e.target.value }))} style={INPUT} />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '0.75rem', color: theme.textMuted, marginBottom: '0.25rem' }}>Emp Code</label>
          <input placeholder="e.g. EMP001" value={filters.emp_code}
            onChange={e => setFilters(f => ({ ...f, emp_code: e.target.value }))} style={INPUT} />
        </div>
        <button onClick={() => load(1)} style={{ background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 1rem', fontSize: '0.875rem', cursor: 'pointer' }}>
          Search
        </button>
        <button onClick={syncRange} style={{ background: '#fff', color: theme.primary, border: `1px solid ${theme.primary}`, borderRadius: '0.5rem', padding: '0.5rem 1rem', fontSize: '0.875rem', cursor: 'pointer' }}>
          🔄 Sync Range
        </button>
      </div>

      <p style={{ fontSize: '0.875rem', color: theme.textMuted, margin: 0 }}>{total} records found</p>

      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
        {loading ? <div style={{ padding: '2rem', textAlign: 'center' }}><Spinner /></div>
        : records.length === 0 ? (
          <p style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted, fontSize: '0.875rem' }}>No records found</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ background: theme.pageBg, borderBottom: `1px solid ${theme.border}` }}>
                  {['Employee', 'Emp Code', 'Date', 'Time', 'Direction', 'Mode', 'Device', 'Temp'].map(h => (
                    <th key={h} style={{ padding: '0.75rem 1rem', textAlign: 'left', fontSize: '0.7rem', fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map((r, i) => (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${theme.border}`, background: i % 2 === 0 ? '#fff' : theme.pageBg }}>
                    <td style={{ padding: '0.65rem 1rem', fontWeight: 500, color: theme.text }}>{r.employee_name || '—'}</td>
                    <td style={{ padding: '0.65rem 1rem', color: theme.textMuted, fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.emp_code || '—'}</td>
                    <td style={{ padding: '0.65rem 1rem', color: theme.textMuted, whiteSpace: 'nowrap' }}>
                      {r.record_time ? new Date(r.record_time).toLocaleDateString('en-IN') : '—'}
                    </td>
                    <td style={{ padding: '0.65rem 1rem', color: theme.text, whiteSpace: 'nowrap' }}>
                      {r.record_time ? new Date(r.record_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td style={{ padding: '0.65rem 1rem' }}>
                      <span style={{
                        padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600,
                        background: r.in_out === 0 ? '#D1FAE5' : '#FEE2E2',
                        color: r.in_out === 0 ? '#065F46' : '#991B1B',
                      }}>{r.in_out_label}</span>
                    </td>
                    <td style={{ padding: '0.65rem 1rem', color: theme.textMuted, fontSize: '0.8rem' }}>{r.mode_label}</td>
                    <td style={{ padding: '0.65rem 1rem', color: theme.textMuted, fontSize: '0.8rem', fontFamily: 'monospace' }}>{r.device_serial || '—'}</td>
                    <td style={{ padding: '0.65rem 1rem', color: theme.textMuted, fontSize: '0.8rem' }}>
                      {r.temperature ? `${r.temperature}°C` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {total > 100 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
          <button onClick={() => load(page - 1)} disabled={page <= 1}
            style={{ padding: '0.4rem 0.75rem', border: `1px solid ${theme.border}`, borderRadius: '0.5rem', background: '#fff', cursor: 'pointer', color: theme.text }}>← Prev</button>
          <span style={{ padding: '0.4rem 0.75rem', color: theme.textMuted, fontSize: '0.875rem' }}>Page {page}</span>
          <button onClick={() => load(page + 1)} disabled={page * 100 >= total}
            style={{ padding: '0.4rem 0.75rem', border: `1px solid ${theme.border}`, borderRadius: '0.5rem', background: '#fff', cursor: 'pointer', color: theme.text }}>Next →</button>
        </div>
      )}
    </div>
  );
}

// ── Devices Tab ───────────────────────────────────────────────────────────────
function DevicesTab() {
  const [devices, setDevices] = useState([]);
  const [status, setStatus]   = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([attendanceAPI.getDevices(), attendanceAPI.getWsStatus()])
      .then(([d, s]) => { setDevices(d.data || []); setStatus(s.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: status?.ams_reachable ? '#10B981' : '#EF4444', flexShrink: 0 }} />
        <div>
          <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: theme.text }}>
            AMS Server — {status?.ams_reachable ? 'Online' : 'Offline'}
          </p>
          <p style={{ margin: 0, fontSize: '0.75rem', color: theme.textMuted }}>{status?.ams_base_url}</p>
        </div>
      </div>

      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
        <div style={{ padding: '1rem', borderBottom: `1px solid ${theme.border}` }}>
          <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: theme.text }}>Registered Devices ({devices.length})</h3>
        </div>
        {devices.length === 0 ? (
          <p style={{ padding: '2rem', textAlign: 'center', color: theme.textMuted, fontSize: '0.875rem' }}>No devices found</p>
        ) : devices.map((d, i) => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 1rem', borderBottom: i < devices.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
            <span style={{ fontSize: '1.5rem' }}>📟</span>
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 500, color: theme.text }}>{d.modelName || 'Unknown'}</p>
              <p style={{ margin: 0, fontSize: '0.75rem', color: theme.textMuted, fontFamily: 'monospace' }}>{d.serialNum}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{
                padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 600,
                background: d.status === 1 ? '#D1FAE5' : '#FEE2E2',
                color: d.status === 1 ? '#065F46' : '#991B1B',
              }}>{d.status === 1 ? 'Online' : 'Offline'}</span>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.72rem', color: theme.textMuted }}>{d.firmware}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Shared Components ─────────────────────────────────────────────────────────
function KpiCard({ label, value, color, icon }) {
  return (
    <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <span style={{ fontSize: '1.25rem' }}>{icon}</span>
        <p style={{ margin: 0, fontSize: '0.75rem', color: theme.textMuted, fontWeight: 500 }}>{label}</p>
      </div>
      <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color }}>{value}</p>
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ width: 32, height: 32, border: `3px solid ${theme.border}`, borderTop: `3px solid ${theme.primary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );
}

// ── Diagnostics Tab ───────────────────────────────────────────────────────────
function DiagnosticsTab() {
  const [diag, setDiag]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [relinking, setRelinking] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    attendanceAPI.getDiagnostics()
      .then(r => setDiag(r.data))
      .catch(() => setDiag(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRelink = async () => {
    setRelinking(true);
    try {
      const r = await attendanceAPI.relink();
      alert(`Re-link done: ${r.data.relinked} records linked, ${r.data.skipped_no_match} still unmatched.`);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Re-link failed.');
    } finally { setRelinking(false); }
  };

  const card = (label, value, color) => (
    <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1rem 1.25rem', minWidth: 140 }}>
      <p style={{ margin: 0, fontSize: '0.72rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</p>
      <p style={{ margin: '0.25rem 0 0', fontSize: '1.5rem', fontWeight: 700, color: color || theme.text }}>{value ?? '—'}</p>
    </div>
  );

  if (loading) return <Spinner />;
  if (!diag)   return <p style={{ color: theme.textMuted, padding: '2rem' }}>Failed to load diagnostics.</p>;

  const amsColor  = diag.ams_reachable ? '#16a34a' : '#dc2626';
  const orphColor = diag.all_time_orphans > 0 ? '#dc2626' : '#16a34a';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* AMS Connection */}
      <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1.25rem' }}>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.78rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase' }}>AMS Connection</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: diag.ams_reachable ? '#dcfce7' : '#fee2e2', color: amsColor, padding: '0.3rem 0.8rem', borderRadius: '9999px', fontWeight: 700, fontSize: '0.82rem' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: amsColor }} />
            {diag.ams_reachable ? 'AMS Online' : 'AMS Unreachable'}
          </span>
          <span style={{ fontSize: '0.82rem', color: theme.textMuted }}>{diag.ams_base_url}</span>
          <span style={{ fontSize: '0.78rem', color: diag.ams_api_key_set ? '#16a34a' : '#dc2626' }}>
            API Key: {diag.ams_api_key_set ? '✓ Set' : '✗ Missing'}
          </span>
        </div>
        {diag.last_punch?.time && (
          <p style={{ margin: '0.75rem 0 0', fontSize: '0.78rem', color: theme.textMuted }}>
            Last punch: <strong>{diag.last_punch.name || diag.last_punch.emp_code}</strong> at {new Date(diag.last_punch.time).toLocaleString('en-IN')}
          </p>
        )}
      </div>

      {/* Today stats */}
      <div>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.78rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase' }}>Today's Sync Status</p>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {card('Total Punches',      diag.today.total_punches)}
          {card('Linked to Employee', diag.today.linked_to_employee, '#16a34a')}
          {card('Orphaned (no emp)',  diag.today.orphaned_no_emp,    diag.today.orphaned_no_emp > 0 ? '#dc2626' : '#16a34a')}
          {card('Attendance Records', diag.today.attendance_records_created, '#2563eb')}
        </div>
      </div>

      {/* Orphaned records */}
      <div style={{ background: '#fff', border: `1px solid ${theme.border}`, borderRadius: '0.75rem', padding: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
          <div>
            <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: 700, color: theme.textMuted, textTransform: 'uppercase' }}>All-time Orphaned Records</p>
            <p style={{ margin: '0.2rem 0 0', fontSize: '1.25rem', fontWeight: 700, color: orphColor }}>{diag.all_time_orphans}</p>
            <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: theme.textMuted }}>Biometric punches with no matching employee (emp_code mismatch)</p>
          </div>
          {diag.all_time_orphans > 0 && (
            <button onClick={handleRelink} disabled={relinking}
              style={{ padding: '0.5rem 1.25rem', background: '#6366F1', color: '#fff', border: 'none', borderRadius: '0.5rem', fontSize: '0.875rem', fontWeight: 600, cursor: relinking ? 'not-allowed' : 'pointer', opacity: relinking ? 0.6 : 1 }}>
              {relinking ? 'Re-linking…' : '🔗 Fix Orphaned Records'}
            </button>
          )}
        </div>

        {diag.unlinked_emp_codes?.length > 0 && (
          <>
            <p style={{ margin: '0 0 0.5rem', fontSize: '0.75rem', fontWeight: 600, color: theme.textMuted }}>Unlinked emp codes (check spelling in employee profile):</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {diag.unlinked_emp_codes.map((u, i) => (
                <span key={i} style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '0.375rem', padding: '0.2rem 0.6rem', fontSize: '0.75rem', color: '#dc2626', fontFamily: 'monospace' }}>
                  {u.emp_code} ({u.person_name})
                </span>
              ))}
            </div>
          </>
        )}

        {diag.all_time_orphans === 0 && (
          <p style={{ margin: 0, fontSize: '0.82rem', color: '#16a34a', fontWeight: 600 }}>✓ All biometric records are linked to employees.</p>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button onClick={load} style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '0.5rem', padding: '0.4rem 1rem', fontSize: '0.82rem', cursor: 'pointer', color: theme.textMuted }}>
          ↻ Refresh
        </button>
      </div>
    </div>
  );
}
